import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle, ShieldCheck, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { apiFetch } from "../api/client";
import { Panel } from "../components/Panel";
import type { Conversation } from "../types";

type DashboardResponse = {
  metrics: {
    open_conversations: number;
    leads: number;
    inbound_messages_today: number;
    blocked_ai_messages: number;
  };
  recent_conversations: Conversation[];
};

export function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => apiFetch<DashboardResponse>("/api/dashboard"),
  });

  if (isLoading) return <p>Caricamento dashboard...</p>;

  const metrics = data?.metrics;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold">Dashboard</h2>
        <p className="mt-1 text-slate-400">Monitoraggio conversazioni, lead e controllo topic.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Metric label="Conversazioni aperte" value={metrics?.open_conversations ?? 0} icon={<MessageCircle />} />
        <Metric label="Lead" value={metrics?.leads ?? 0} icon={<Users />} />
        <Metric label="Messaggi oggi" value={metrics?.inbound_messages_today ?? 0} icon={<MessageCircle />} />
        <Metric label="Fuori tema" value={metrics?.blocked_ai_messages ?? 0} icon={<ShieldCheck />} />
      </div>

      <Panel>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Conversazioni recenti</h3>
          <Link to="/dahsbot/conversations" className="text-sm text-emerald-300 hover:text-emerald-200">
            Vedi tutte
          </Link>
        </div>
        <div className="space-y-3">
          {(data?.recent_conversations ?? []).map((conversation) => (
            <div
              key={conversation.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 p-4"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{conversation.lead?.profile_name ?? conversation.lead?.phone}</p>
                <p className="mt-1 truncate text-sm text-slate-400">
                  {conversation.latest_message?.body ?? "Nessun messaggio"}
                </p>
              </div>
              <Link
                to={`/dahsbot/conversations/${conversation.id}`}
                className="shrink-0 rounded-xl bg-emerald-400 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-300"
              >
                Apri chat
              </Link>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function Metric({ label, value, icon }: { label: string; value: number; icon: ReactNode }) {
  return (
    <Panel>
      <div className="flex items-center justify-between text-slate-400">
        <span className="text-sm">{label}</span>
        <span className="h-5 w-5">{icon}</span>
      </div>
      <p className="mt-4 text-3xl font-semibold text-white">{value}</p>
    </Panel>
  );
}
